import { Box, useTheme } from '@mui/joy';
import { ChartOptions } from 'chart.js';
import { merge } from 'lodash';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

import { SkeletonChart } from 'reports/components';

export interface IHorizontalBarChartDataset {
  backgroundColor: string;
  data: number[];
  label: string;
}

export interface IHorizontalBarChartData {
  datasets: IHorizontalBarChartDataset[];
  labels: string[];
}

interface IProps {
  data: IHorizontalBarChartData;
  height?: number | string;
  isLoading?: boolean;
  options?: ChartOptions<'bar'>;
}

export const HorizontalBarChart = ({ data, height, isLoading, options }: IProps) => {
  const theme = useTheme();
  const textColor = theme.palette.text.primary;
  const gridColor = theme.palette.neutral[200];

  const defaultOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      indexAxis: 'y',
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          labels: {
            color: textColor,
          },
          position: 'bottom',
        },
        tooltip: {
          enabled: true,
        },
      },
      responsive: true,
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            color: gridColor,
          },
          stacked: true,
          ticks: {
            color: textColor,
          },
        },
        y: {
          grid: {
            display: false,
          },
          stacked: true,
          ticks: {
            color: textColor,
          },
        },
      },
    }),
    [gridColor, textColor]
  );

  const mergedOptions = options ? merge({}, defaultOptions, options) : defaultOptions;

  if (isLoading) {
    return <SkeletonChart height={height} />;
  }

  return (
    <Box sx={{ height }}>
      <Bar data={data} options={mergedOptions} />
    </Box>
  );
};
